import { useCallback, useEffect, useMemo, useState } from 'react';
import * as Location from 'expo-location';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';

import { useGlobalAppState } from '../../../context/GlobalAppState';
import { checkIfLocationTooFar } from '../../../helpers/location-distance';
import { useTooFarLocationThresholdInMiles } from '../../../hooks/useTooFarLocationThresholdInMiles';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Evaluates whether the selected location is too far from the device and shows
 * the tooltip at most once for the current app session.
 */
export const useLocationTooltip = (params: Params) => {
  const { canShow = true, channel, locationId, targetPosition } = params;

  const { hasShownLocationTooltipRef } = useGlobalAppState();
  const { track } = useTelemetry();

  // ─── Flags ───────────────────────────────────────────────────────────

  const isLocationTooltipEnabled = useFeatureFlag('CELS-5470-order-location-tooltip-enabled');
  const tooFarThresholdInMiles = useTooFarLocationThresholdInMiles({ channel, targetPosition });

  // ─── State ───────────────────────────────────────────────────────────

  const [visibleLocationIdentity, setVisibleLocationIdentity] = useState<string>();

  // ─── Derived ─────────────────────────────────────────────────────────

  const locationIdentity = channel && locationId ? `${channel}:${locationId}` : undefined;
  const targetLatitude = targetPosition?.latitude;
  const targetLongitude = targetPosition?.longitude;
  const hasLocationIdentity = Boolean(locationIdentity);
  const hasTargetPosition = targetLatitude !== undefined && targetLongitude !== undefined;
  const hasValidDistanceThreshold = tooFarThresholdInMiles > 0;

  const isVisible = Boolean(locationIdentity && visibleLocationIdentity === locationIdentity);

  const canEvaluateDistance =
    canShow &&
    isLocationTooltipEnabled &&
    hasLocationIdentity &&
    hasTargetPosition &&
    hasValidDistanceThreshold;

  const distanceEvaluation = useMemo(() => {
    if (!canEvaluateDistance || !locationIdentity || !hasTargetPosition) return;

    return {
      locationIdentity,
      targetPosition: { latitude: targetLatitude, longitude: targetLongitude },
      thresholdInMiles: tooFarThresholdInMiles,
    };
  }, [
    canEvaluateDistance,
    locationIdentity,
    hasTargetPosition,
    targetLatitude,
    targetLongitude,
    tooFarThresholdInMiles,
  ]);

  // ─── Handlers ────────────────────────────────────────────────────────

  const dismiss = useCallback(() => {
    if (!channel || !isVisible) return;

    setVisibleLocationIdentity(undefined);
    track('location_tooltip_dismissed', { channel });
  }, [channel, isVisible, track]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    setVisibleLocationIdentity(undefined);

    async function evaluateDistance() {
      if (!distanceEvaluation || hasShownLocationTooltipRef.current) return;

      const currentPosition = await getCurrentPositionCoordinates();

      if (!currentPosition || hasShownLocationTooltipRef.current) return;

      const { locationIdentity, targetPosition, thresholdInMiles } = distanceEvaluation;

      const isTooFar = checkIfLocationTooFar({
        currentPosition,
        targetPosition,
        thresholdInMiles,
      });

      if (!isTooFar) return;

      hasShownLocationTooltipRef.current = true;
      setVisibleLocationIdentity(locationIdentity);
    }

    void evaluateDistance();
  }, [distanceEvaluation, hasShownLocationTooltipRef]);

  // ─────────────────────────────────────────────────────────────────────

  return { dismiss, isVisible };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

async function getCurrentPositionCoordinates(): Promise<PositionCoordinates | undefined> {
  try {
    const permission = await Location.getForegroundPermissionsAsync();

    if (!permission.granted) {
      return;
    }

    const lastKnownPosition = await Location.getLastKnownPositionAsync(LAST_KNOWN_POSITION_OPTIONS);

    if (lastKnownPosition) {
      return lastKnownPosition.coords;
    }

    const currentPosition = await Location.getCurrentPositionAsync(CURRENT_POSITION_OPTIONS);

    return currentPosition.coords;
  } catch {}
}

// ─── Constants ───────────────────────────────────────────────────────────────

const LAST_KNOWN_POSITION_OPTIONS: Location.LocationLastKnownOptions = {
  maxAge: 60 * 60 * 1000,
  requiredAccuracy: 500,
};

const CURRENT_POSITION_OPTIONS: Location.LocationOptions = {
  accuracy: Location.Accuracy.Low,
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  canShow?: boolean;
  channel?: OrderChannel;
  locationId?: string;
  targetPosition?: PositionCoordinates;
};

type OrderChannel = 'delivery' | 'pickup';

type PositionCoordinates = {
  latitude: number;
  longitude: number;
};
